import { useEffect, useState, useCallback, useRef } from "react";
import { useAlarm, startRingtone, stopRingtone, SNOOZE_PRESETS_MS } from "src/Context/AlarmContext";

function formatTime(d) {
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export default function AlarmPopup() {
  const { alarm, dismiss, snooze } = useAlarm();
  const [now, setNow] = useState(() => new Date());
  const voiceAudioRef = useRef(null);

  useEffect(() => {
    if (!alarm) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [alarm]);

  useEffect(() => {
    if (!alarm) {
      stopRingtone();
      return;
    }
    startRingtone();
    return () => stopRingtone();
  }, [alarm?.id]);

  useEffect(() => {
    return () => {
      if (voiceAudioRef.current) {
        try {
          voiceAudioRef.current.pause();
          voiceAudioRef.current = null;
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  const playVoiceClip = useCallback((pathOrUrl) => {
    if (!pathOrUrl) return;
    try {
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        voiceAudioRef.current = null;
      }
      const audio = new Audio(pathOrUrl);
      audio.volume = 1;
      audio.play().catch(() => {});
      voiceAudioRef.current = audio;
    } catch {
      /* ignore */
    }
  }, []);

  const onDismiss = useCallback(() => {
    dismiss();
    if (voiceAudioRef.current) {
      try {
        voiceAudioRef.current.pause();
        voiceAudioRef.current = null;
      } catch {
        /* ignore */
      }
    }
  }, [dismiss]);

  const overlayRef = useRef(null);

  useEffect(() => {
    if (alarm) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [alarm]);

  useEffect(() => {
    if (!alarm) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onDismiss();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [alarm, onDismiss]);

  if (!alarm) return null;

  const voiceUrls = Array.isArray(alarm.voiceUrls) ? alarm.voiceUrls : [];
  const clockSize = "clamp(2.25rem, 7vw, 3.5rem)";

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[2147483640] flex items-center justify-center bg-black/90 backdrop-blur-lg p-4 sm:p-6 animate-in fade-in duration-300"
      role="dialog"
      aria-label="Alarm notification"
      aria-modal="true"
    >
      <div className="flex flex-col w-full max-w-md mx-auto">
        <div className="flex flex-col items-center gap-3 text-center">
          <p
            style={{ fontSize: clockSize }}
            className="font-light tracking-wider text-white/90 tabular-nums"
          >
            {formatTime(now)}
          </p>

          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-purple/80">
            Doitify alarm
          </p>

          <h2 className="text-2xl sm:text-[1.625rem] font-bold text-white leading-tight">
            {alarm.title}
          </h2>

          {alarm.body && (
            <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-sm">
              {alarm.body}
            </p>
          )}

          {voiceUrls.length > 0 && (
            <div className="w-full mt-2">
              <p className="text-[0.6875rem] font-bold uppercase tracking-wider text-white/40 mb-3">
                Voice
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {voiceUrls.map((u, i) => (
                  <button
                    key={`${u}-${i}`}
                    type="button"
                    onClick={() => playVoiceClip(u)}
                    className="px-4 py-2.5 rounded-xl border border-white/20 text-white font-semibold text-sm hover:bg-white/10 active:scale-[0.98] transition-all"
                  >
                    Play clip {i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={onDismiss}
            className="w-full max-w-xs py-4 rounded-2xl bg-[var(--purple)] text-white text-lg font-extrabold hover:brightness-110 active:scale-[0.98] transition-all shadow-lg shadow-purple/30"
            style={{
              boxShadow: "0 8px 32px rgba(var(--purple-rgb, 111, 56, 197), 0.35)",
            }}
          >
            I understand
          </button>

          <div className="flex flex-wrap gap-2.5 justify-center">
            {[
              { ms: SNOOZE_PRESETS_MS[0], label: "5s" },
              { ms: SNOOZE_PRESETS_MS[1], label: "5m" },
              { ms: SNOOZE_PRESETS_MS[2], label: "30m" },
              { ms: SNOOZE_PRESETS_MS[3], label: "60m" },
              { ms: SNOOZE_PRESETS_MS[4], label: "120m" },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => snooze(opt.ms)}
                className="px-3.5 py-2.5 rounded-xl border border-white/20 text-white/80 font-semibold text-xs hover:bg-white/10 hover:border-white/30 active:scale-[0.98] transition-all"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
